/**
 * @file Main Application Entry Point - Conditional Logic Only
 * @description Handles page-specific initialization and conditional logic
 * Global logic has been moved to common.js
 */

import "./utils/safe-console.js";

// Import core modules
import { App } from "./core/App.js";

// Import common functionality (global logic moved here)
import { initCommon } from "./common.js";

// Import FacetWP components
import "./facetwp/country-mode-switch.js";

// Import post-type modules (conditional loading)
import { initIndustrySingleMap } from "./post-types/industry/single-map.js";
import { initIndustryListing } from "./post-types/industry/listing.js";
import { initIndustryCollection } from "./post-types/industry/collection.js";

// Import bottom sheet component for home page
import BottomSheet from "./components/bottom-sheet.js";

let app = null;

/**
 * Check if current device/viewport is mobile
 * Using viewport width as primary indicator
 */
function isMobileDevice() {
  // Method 1: Check viewport width (primary)
  const viewportWidth = window.innerWidth;
  const mobileBreakpoint = 1024; // lg breakpoint in Tailwind CSS

  if (viewportWidth < mobileBreakpoint) {
    return true;
  }

  // Method 2: Check user agent as fallback (for edge cases)
  const userAgent = navigator.userAgent.toLowerCase();
  const mobileKeywords = ["mobile", "android", "iphone", "ipad", "tablet", "touch"];
  const isMobileUA = mobileKeywords.some((keyword) => userAgent.includes(keyword));

  // Method 3: Check for touch capability (additional indicator)
  const isTouchDevice = "ontouchstart" in window || navigator.maxTouchPoints > 0;

  // Return true if any mobile indicator is present AND viewport is small-ish
  return (isMobileUA || isTouchDevice) && viewportWidth <= 1200;
}

/**
 * Initialize the application with conditional logic only
 * Global logic is handled by common.js
 */
async function initApp() {
  try {
    console.debug("IPHUB App: Initializing conditional logic...");

    // Initialize common functionality first (global logic)
    await initCommon();

    // Initialize core application
    app = new App();
    await app.init();

    // Initialize collections feature (if user has permission)
    if (window.iphubCollections?.isLoggedIn) {
      console.debug("IPHUB App: Initializing Collections feature...");
      const { initCollections } = await import("./features/collections/index.js");
      await initCollections();
    }

    // Initialize post-type modules based on current page (conditional logic)
    const currentFeature = app.getCurrentFeature();

    // Initialize bottom sheet for all pages (mobile only)
    if (isMobileDevice()) {
      console.debug("IPHUB App: Initializing bottom sheet (mobile)...");
      await initBottomSheet();
    } else {
      console.debug("IPHUB App: Skipping bottom sheet initialization (desktop)...");
    }

    // Note: FDI archive functionality removed - using features/fdi-map/ instead
    if (currentFeature === "industry-single") {
      console.debug("IPHUB App: Initializing Industry single functionality...");
      await initIndustrySingleMap();
    } else if (currentFeature === "industry-archive") {
      console.debug("IPHUB App: Initializing Industry archive functionality...");
      await Promise.all([initIndustryListing(), initIndustryCollection()]);
    }

    // Initialize single location map if on industry single page
    if (document.getElementById("osm-single-wrapper")) {
      console.debug("IPHUB App: Initializing single location map...");
      const { initSingleLocationMap } = await import("./features/single-location-map/index.js");
      await initSingleLocationMap();
    }

    // Initialize location list if on search/location pages or taxonomy pages
    if (
      document.getElementById("osm-wrapper") &&
      (window.location.pathname.includes("search") ||
        window.location.pathname.includes("location") ||
        document.body.classList.contains("tax-industry_location"))
    ) {
      console.debug("IPHUB App: Initializing location list...");
      const { initLocationList } = await import("./features/location-list/index.js");
      await initLocationList();
    }

    console.debug("IPHUB App: Conditional initialization completed successfully");
    return app;
  } catch (error) {
    console.error("IPHUB App: Conditional initialization failed:", error);
    throw error;
  }
}

/**
 * Initialize bottom sheet component for all pages
 */
async function initBottomSheet() {
  try {
    const bottomSheetContainer = document.querySelector("[data-bottomsheet]");

    if (!bottomSheetContainer) {
      console.warn("IPHUB App: Bottom sheet container not found");
      return;
    }

    // Initialize bottom sheet with configuration
    const bottomSheet = new BottomSheet(bottomSheetContainer, {
      enableGestures: true,
      enableKeyboard: true,
      initialState: "collapsed",
      debug: true, // Enable debug mode for development
    });

    // Listen for state changes
    bottomSheetContainer.addEventListener("bottomsheet:statechange", (e) => {
      const { previousState, newState } = e.detail;
      console.debug(`Bottom sheet state changed: ${previousState} -> ${newState}`);

      // Dispatch custom event for other components (e.g., map)
      document.dispatchEvent(
        new CustomEvent("bottomsheet:change", {
          detail: { state: newState, previousState },
        }),
      );
    });

    // Store reference globally for other modules to access
    window.IPHUB_BOTTOM_SHEET = bottomSheet;

    // Add resize handler to disable/enable bottom sheet based on viewport
    const resizeHandler = () => {
      const isMobile = isMobileDevice();
      const hasBottomSheet = window.IPHUB_BOTTOM_SHEET;

      if (!isMobile && hasBottomSheet) {
        // Destroy bottom sheet if viewport becomes desktop
        console.debug("IPHUB App: Viewport changed to desktop, destroying bottom sheet...");
        window.IPHUB_BOTTOM_SHEET.destroy();
        window.IPHUB_BOTTOM_SHEET = null;
      } else if (isMobile && !hasBottomSheet) {
        // Re-initialize bottom sheet if viewport becomes mobile
        console.debug("IPHUB App: Viewport changed to mobile, re-initializing bottom sheet...");
        initBottomSheet();
      }
    };

    // Debounce resize handler to avoid excessive calls
    let resizeTimeout;
    const debouncedResizeHandler = () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(resizeHandler, 250);
    };

    window.addEventListener("resize", debouncedResizeHandler);

    console.debug("IPHUB App: Bottom sheet initialized successfully");
  } catch (error) {
    console.error("IPHUB App: Failed to initialize bottom sheet:", error);
  }
}

/**
 * Get application instance
 */
function getApp() {
  return app;
}

/**
 * Get application status
 */
function getAppStatus() {
  if (!app) {
    return { initialized: false, type: "none" };
  }

  return {
    initialized: app.isInitialized,
    type: "conditional",
    currentFeature: app.getCurrentFeature(),
    modules: app.getLoadedModules(),
  };
}

// Initialize when DOM is ready
if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", initApp);
} else {
  initApp();
}

// Export for global access
window.IPHUB_APP = {
  init: initApp,
  getApp: getApp,
  getStatus: getAppStatus,
  isMobile: isMobileDevice,
  bottomSheet: {
    isEnabled: () => !!window.IPHUB_BOTTOM_SHEET,
    getInstance: () => window.IPHUB_BOTTOM_SHEET,
    forceInit: initBottomSheet,
    forceDestroy: () => {
      if (window.IPHUB_BOTTOM_SHEET) {
        window.IPHUB_BOTTOM_SHEET.destroy();
        window.IPHUB_BOTTOM_SHEET = null;
      }
    },
  },
};

// Export individual functions for module access
export { initApp, getApp, getAppStatus };
